/**
 * Exit Intent Popup
 * Shows popup after inactivity delay
 * Only shows once per session (when not in dev)
 */

(function() {
  'use strict';

  // --- DEV TESTING: set to true to test popup (short delay, can show multiple times) ---
  const DEV_MODE = false;  // set to false for production
  // ------------------------------------------------------------------------------------

  const STORAGE_KEY = 'exit_intent_popup_shown';
  // Production: 10s delay. In dev, use short delay so you can test without waiting.
  const INACTIVITY_TIME = DEV_MODE ? 2000 : 12000; // 12 seconds in milliseconds

  let inactivityTimer = null;
  let hasShownPopup = false;
  let userActivity = false;

  // Check if popup has already been shown (stored in localStorage)
  function checkSessionStorage() {
    if (DEV_MODE) return false; // Allow showing again when testing
    const shown = localStorage.getItem(STORAGE_KEY);
    return shown === 'true';
  }

  // Mark popup as shown in localStorage
  function markAsShown() {
    if (!DEV_MODE) localStorage.setItem(STORAGE_KEY, 'true');
    hasShownPopup = true;
  }

  // Reset inactivity timer
  function resetInactivityTimer() {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }

    // Only set timer if popup hasn't been shown
    if (!hasShownPopup && !checkSessionStorage()) {
      inactivityTimer = setTimeout(() => {
        if (!userActivity && !hasShownPopup && !checkSessionStorage()) {
          showPopup();
        }
      }, INACTIVITY_TIME);
    }
  }

  // Show the popup
  function showPopup() {
    if (hasShownPopup || checkSessionStorage()) {
      return;
    }

    const popup = document.getElementById('exit-intent-popup');
    if (!popup) {
      return;
    }

    // Re-populate options with a fresh shuffle each time the popup is shown
    populateOptions();
    attachOtherFieldVisibility(popup);

    popup.classList.add('active');
    markAsShown();

    // Prevent body scroll when popup is open
    document.body.style.overflow = 'hidden';

    // Focus on first checkbox for accessibility
    const firstCheckbox = popup.querySelector('input[type="checkbox"]');
    if (firstCheckbox) {
      firstCheckbox.focus();
    }
  }

  // Hide the popup
  function hidePopup() {
    const popup = document.getElementById('exit-intent-popup');
    if (popup) {
      popup.classList.remove('active');
      document.body.style.overflow = '';
      if (DEV_MODE) hasShownPopup = false; // Allow showing again when testing
    }
  }

  // Track user activity
  function trackActivity() {
    userActivity = true;

    // Reset activity flag after a short delay
    setTimeout(() => {
      userActivity = false;
    }, 100);

    resetInactivityTimer();
  }

  // Google Sheets Web App URL
  // TODO: Replace this with your Google Apps Script Web App URL after deployment
  const GOOGLE_SHEETS_WEB_APP_URL = 'https://script.google.com/a/macros/diversido.io/s/AKfycby9ofr1IhgYEC292h-VnfN4O6n59HaMDhvbxdTLi0eKh_1HbEta3T5HgXles4N713SI/exec'; // Paste your Web App URL here

  // Handle form submission
  function handleFormSubmit(e) {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);
    // Get all selected checkboxes (multi-choice)
    const selectedReasons = formData.getAll('exit-reason');
    const otherDetails = formData.get('other-details');
    const exitReasonsString = selectedReasons.length > 0 ? selectedReasons.join(', ') : '';

    // Prepare data to send to Google Sheets
    const submissionData = {
      // Keep both keys for compatibility with existing Google Sheet setup
      exitReasons: exitReasonsString,
      exitReason: exitReasonsString,
      otherDetails: otherDetails || '',
      pageUrl: window.location.href,
      userAgent: navigator.userAgent
    };

    // Send data to Google Sheets if URL is configured
    if (GOOGLE_SHEETS_WEB_APP_URL) {
      fetch(GOOGLE_SHEETS_WEB_APP_URL, {
        method: 'POST',
        mode: 'no-cors', // Required for Google Apps Script (can't read response)
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      }).catch(error => {
        // Silently handle errors (no-cors mode doesn't allow reading response)
      });
    }

    // Give user feedback before closing popup
    const submitButton = form.querySelector('.exit-intent-popup__submit');
    if (submitButton) {
      const originalText = submitButton.textContent;
      submitButton.disabled = true;
      submitButton.textContent = 'Thank you!';

      setTimeout(() => {
        hidePopup();
        // In case popup is shown again in a new session, restore button state
        submitButton.disabled = false;
        submitButton.textContent = originalText;
      }, 1000);
    } else {
      hidePopup();
    }
  }

  // Populate options from data attribute
  function populateOptions() {
    const optionsContainer = document.querySelector('.exit-intent-popup__options');
    if (!optionsContainer) {
      return;
    }

    const optionsText = optionsContainer.getAttribute('data-options-text');
    if (!optionsText) {
      return;
    }

    // Split by newlines (handle both \r\n and \n)
    let options = optionsText
      .split(/\r\n|\r|\n/)
      .map(opt => opt.trim())
      .filter(opt => opt.length > 0);

    // Ensure we always have a "Reason not listed" option at the end
    const hasReasonNotListed = options.some(
      opt => opt.toLowerCase() === 'reason not listed'
    );
    if (!hasReasonNotListed) {
      options.push('Reason not listed');
    }

    // Shuffle all options except the last one ("Reason not listed")
    const lastOption = options.length > 0 ? options.pop() : null;
    if (options.length > 1) {
      for (let i = options.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [options[i], options[j]] = [options[j], options[i]];
      }
    }
    if (lastOption) options.push(lastOption);

    // Clear container and populate with checkboxes
    optionsContainer.innerHTML = '';

    options.forEach((option, index) => {
      const label = document.createElement('label');
      // Add 'odd-option' class for 1st, 3rd, 5th, etc. (0-indexed: 0, 2, 4, etc.)
      const className = index % 2 === 0 ? 'exit-intent-popup__option odd-option' : 'exit-intent-popup__option';
      label.className = className;

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.name = 'exit-reason';
      input.value = option;

      // Special handling for "Reason not listed" option
      if (option.toLowerCase() === 'reason not listed') {
        input.id = 'exit-intent-reason-not-listed';
        label.className += ' exit-intent-popup__option--custom';
      }

      const span = document.createElement('span');
      span.textContent = option;

      label.appendChild(input);
      label.appendChild(span);
      optionsContainer.appendChild(label);
    });
  }

  // Attach "Reason not listed" checkbox → textarea visibility. Call after every populateOptions()
  // (options DOM is replaced on each run, so the checkbox reference and listener must be re-attached.)
  function attachOtherFieldVisibility(popup) {
    const otherField = popup.querySelector('.exit-intent-popup__other-field');
    const otherReasonCheckbox = popup.querySelector('#exit-intent-reason-not-listed');

    if (otherField && otherReasonCheckbox) {
      const updateOtherVisibility = () => {
        if (otherReasonCheckbox.checked) {
          otherField.classList.add('is-visible');
        } else {
          otherField.classList.remove('is-visible');
        }
      };
      otherReasonCheckbox.addEventListener('change', updateOtherVisibility);
      updateOtherVisibility();
    }
  }

  // Initialize popup functionality
  function init() {
    // Check if popup element exists
    const popup = document.getElementById('exit-intent-popup');
    if (!popup) {
      return;
    }

    // Populate options from data attribute
    populateOptions();
    attachOtherFieldVisibility(popup);

    // Textarea placeholder behavior (once; textarea is not recreated when options are re-populated)
    const otherTextarea = popup.querySelector('#exit-intent-other');
    const otherField = popup.querySelector('.exit-intent-popup__other-field');
    if (otherTextarea && otherField) {
      const originalPlaceholder = otherTextarea.getAttribute('placeholder') || '';
      otherTextarea.addEventListener('focus', function() {
        otherField.classList.add('is-expanded');
        otherTextarea.setAttribute('placeholder', 'Write here...');
      });
      otherTextarea.addEventListener('blur', function() {
        otherTextarea.setAttribute('placeholder', originalPlaceholder);
      });
    }

    // Check if already shown in this session
    if (checkSessionStorage()) {
      hasShownPopup = true;
      return;
    }

    // Close button handler
    const closeButton = popup.querySelector('.exit-intent-popup__close');
    if (closeButton) {
      closeButton.addEventListener('click', hidePopup);
    }

    // Backdrop click handler
    const backdrop = popup.querySelector('.exit-intent-popup__backdrop');
    if (backdrop) {
      backdrop.addEventListener('click', hidePopup);
    }

    // Form submission handler
    const form = popup.querySelector('#exit-intent-form');
    if (form) {
      form.addEventListener('submit', handleFormSubmit);
    }

    // Prevent popup from closing when clicking inside the container
    const container = popup.querySelector('.exit-intent-popup__container');
    if (container) {
      container.addEventListener('click', function(e) {
        e.stopPropagation();
      });
    }

    // Track user activity events
    const activityEvents = [
      'mousedown',
      'mousemove',
      'scroll',
      'wheel',
      'touchstart',
      'touchmove',
      'click',
      'keypress',
      'keydown',
      'input'
    ];
    activityEvents.forEach(event => {
      document.addEventListener(event, trackActivity, { passive: true });
    });

    // Handle escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && popup.classList.contains('active')) {
        hidePopup();
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();